import type { FC } from 'react';

import { useMemo } from 'react';
import styled from 'styled-components';

import { S as RecommendationsStyles } from './styles';
import type { FitRecommendations as FitRecommendationsProps } from '~/amplienceTypes/schemas/imported/fit-recommendations-schema';
import { Carousel } from '~/components/shared/core/carousel/Carousel';
import { CarouselScroll } from '~/components/shared/core/carousel/CarouselScroll';
import { CarouselScrollItem } from '~/components/shared/core/carousel/CarouselScrollItem';
import { CarouselTitle } from '~/components/shared/core/carousel/CarouselTitle';
import FitTile from '~/components/shared/recommendations/tile/FitTile';
import { FITS } from '~/utilities/constants/algolia';
import { usePDPContext } from '~/utilities/context/static/PDPContext';

const CAROUSEL_ID = 'fit-recommendations';

const S = {
  CarouselWrapper: styled(RecommendationsStyles.RecommendationsWrapper)`
    --product-tile-info-height: 0px;

    padding-bottom: ${({ theme }) => theme.padding.lg};
  `,
};

export const FitRecommendations: FC<FitRecommendationsProps> = ({
  visibleOnTablet,
  visibleOnMobile,
  visibleOnDesktop,
  title,
  fitConfiguration,
  ariaLabel,
  tileAriaLabel,
}) => {
  const { categoryDetails, product } = usePDPContext();

  const recommendations = useMemo(() => {
    const fitCategory = categoryDetails?.categoryCode?.split('_')?.[0] ?? '';
    const fits = fitConfiguration?.[fitCategory];

    if (!fits || !Array.isArray(fits)) {
      return [];
    }

    const facetData = categoryDetails?.visibleFacetsV2?.find(facet => facet?.code === FITS);
    const facetValues = facetData?.facetValues ?? [];

    const sortedFits = [
      ...fits.filter(item => item?.code === product?.fitEN),
      ...fits.filter(item => item?.code !== product?.fitEN),
    ];

    return sortedFits
      .map(({ code, image }) => {
        const facet = facetValues?.find(item => item?.code === code);

        return { ...facet, image };
      })
      .filter(({ url, image }) => url && image);
  }, [categoryDetails, fitConfiguration, product]);

  if (!recommendations.length) {
    return null;
  }

  return (
    <S.CarouselWrapper>
      <Carousel
        visibleOnTablet={visibleOnTablet}
        visibleOnMobile={visibleOnMobile}
        visibleOnDesktop={visibleOnDesktop}
        title={<CarouselTitle id={CAROUSEL_ID}>{title}</CarouselTitle>}
        id={CAROUSEL_ID}
      >
        <CarouselScroll id={CAROUSEL_ID} ariaLabel={ariaLabel}>
          {recommendations?.map(({ code, image, url }, index) => (
            <CarouselScrollItem key={code} index={index}>
              {props => (
                <FitTile
                  code={code}
                  image={image}
                  url={url!}
                  tileAriaLabel={tileAriaLabel}
                  {...props}
                />
              )}
            </CarouselScrollItem>
          ))}
        </CarouselScroll>
      </Carousel>
    </S.CarouselWrapper>
  );
};
